import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import aiApi, { streamAiTask } from '../../../utils/aiApi';
import AIComposePrompt from './AIComposePrompt';
import AIComposePreview from './AIComposePreview';
import {
  DEFAULT_COMPOSE_LENGTHS,
  DEFAULT_COMPOSE_PURPOSES,
  DEFAULT_COMPOSE_TONES,
  DEFAULT_LANGUAGES,
  loadComposePrefs,
  parseComposeOutput,
  saveComposePrefs,
  textToHtml,
} from './composeHelpers';
import './aiCompose.css';

/**
 * Right-side AI Email Composer drawer.
 * Generates subject + body via OpenRouter streaming; never sends mail.
 */
export default function AIComposeModal({
  open,
  onClose,
  onInsert,
  senderName = '',
  recipientHint = '',
}) {
  const prefs = useMemo(() => loadComposePrefs(), []);
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState(prefs.tone);
  const [length, setLength] = useState(prefs.length);
  const [language, setLanguage] = useState(prefs.language);
  const [purpose, setPurpose] = useState(prefs.purpose);
  const [rawText, setRawText] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState('');
  const [options, setOptions] = useState({
    tones: DEFAULT_COMPOSE_TONES,
    lengths: DEFAULT_COMPOSE_LENGTHS,
    languages: DEFAULT_LANGUAGES,
    purposes: DEFAULT_COMPOSE_PURPOSES,
  });
  const abortRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    aiApi.getSettings()
      .then((res) => {
        if (cancelled || !res?.success) return;
        const data = res.data || {};
        setOptions({
          tones: data.composeTones?.length ? data.composeTones : DEFAULT_COMPOSE_TONES,
          lengths: data.composeLengths?.length ? data.composeLengths : DEFAULT_COMPOSE_LENGTHS,
          languages: data.languages?.length
            ? ['Auto', ...data.languages.filter((l) => l !== 'Auto')]
            : DEFAULT_LANGUAGES,
          purposes: data.composePurposes?.length ? data.composePurposes : DEFAULT_COMPOSE_PURPOSES,
        });
        if (data.enabled === false) {
          setError('AI Assistant is disabled. Ask an admin to enable it.');
        }
      })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  useEffect(() => () => abortRef.current?.(), []);

  const persistPrefs = (next) => {
    saveComposePrefs(next);
  };

  const buildPayload = (action = 'generate', extra = {}) => ({
    action,
    prompt,
    text: extra.text ?? (subject || body ? `SUBJECT: ${subject}\n\n${body}` : ''),
    tone,
    length,
    purpose,
    language,
    senderName,
    recipientHint,
    ...extra,
  });

  const runStream = (payload) => {
    abortRef.current?.();
    setLoading(true);
    setStreaming(true);
    setError('');
    setRawText('');
    setSubject('');
    setBody('');

    abortRef.current = streamAiTask('compose', payload, {
      onDelta: (delta) => {
        setLoading(false);
        setRawText((prev) => {
          const next = prev + delta;
          const parsed = parseComposeOutput(next);
          setSubject(parsed.subject);
          setBody(parsed.body);
          return next;
        });
      },
      onDone: (done) => {
        setStreaming(false);
        setLoading(false);
        if (done?.subject || done?.body) {
          setSubject(done.subject || '');
          setBody(done.body || '');
        } else {
          setRawText((prev) => {
            const parsed = parseComposeOutput(prev);
            setSubject(parsed.subject);
            setBody(parsed.body);
            return prev;
          });
        }
      },
      onError: (e) => {
        setError(e.message || 'AI compose failed');
        setStreaming(false);
        setLoading(false);
      },
    });
  };

  const handleGenerate = () => {
    if (!String(prompt || '').trim()) return;
    persistPrefs({ tone, language, length, purpose });
    runStream(buildPayload('generate'));
  };

  const handleAction = async (actionId) => {
    if (actionId === 'copy') {
      const text = subject ? `Subject: ${subject}\n\n${body}` : body;
      try {
        await navigator.clipboard.writeText(text);
      } catch (_) {
        setError('Could not copy to clipboard');
      }
      return;
    }

    if (actionId === 'insert') {
      onInsert?.({
        subject,
        bodyHtml: textToHtml(body),
        bodyText: body,
      });
      return;
    }

    if (actionId === 'regenerate') {
      handleGenerate();
      return;
    }

    if (!body && !subject) return;
    const draft = `SUBJECT: ${subject || ''}\n\n${body || ''}`;
    runStream(buildPayload(actionId, { text: draft }));
  };

  if (!open) return null;

  return createPortal(
    <div
      className="aic-overlay"
      ref={overlayRef}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose?.();
      }}
      role="presentation"
    >
      <aside
        className="aic-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="AI Email Composer"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="aic-header">
          <div className="aic-title-wrap">
            <h2>AI Email Composer</h2>
            <p>Optional draft helper — review and edit before sending. Never sends automatically.</p>
          </div>
          <button type="button" className="aic-close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </header>

        <div className="aic-body">
          <AIComposePrompt
            prompt={prompt}
            onPromptChange={setPrompt}
            tone={tone}
            length={length}
            language={language}
            purpose={purpose}
            onToneChange={(v) => { setTone(v); persistPrefs({ tone: v, language, length, purpose }); }}
            onLengthChange={(v) => { setLength(v); persistPrefs({ tone, language, length: v, purpose }); }}
            onLanguageChange={(v) => { setLanguage(v); persistPrefs({ tone, language: v, length, purpose }); }}
            onPurposeChange={(v) => { setPurpose(v); persistPrefs({ tone, language, length, purpose: v }); }}
            tones={options.tones}
            lengths={options.lengths}
            languages={options.languages}
            purposes={options.purposes}
            disabled={loading || streaming}
            loading={loading || streaming}
            onGenerate={handleGenerate}
          />

          <AIComposePreview
            subject={subject}
            body={body || (!subject ? rawText : '')}
            streaming={streaming}
            loading={loading}
            error={error}
            disabled={loading || streaming}
            onAction={handleAction}
          />

          <details className="aic-settings">
            <summary>Composer defaults</summary>
            <p style={{ fontSize: 12.5, color: '#5f6368', margin: '0 0 8px' }}>
              Tone, length, and language selections are remembered on this device.
              Model routing is controlled by admin AI settings (with automatic fallback).
            </p>
          </details>
        </div>
      </aside>
    </div>,
    document.body
  );
}
